import React, { useEffect, useState, useRef, useCallback } from "react";
import "../../styles/Kiosk.css";
import { resolveApiUrl } from "../../utils/useApiStable";

const TOKEN_KEY = "kioskToken";
const CEC_LOCAL_PORT = localStorage.getItem("cecLocalPort") || "8765";

const generateToken = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
  });
};

const shortCode = (token) => token.replace(/-/g, "").substring(0, 8).toUpperCase();

const resolvePublicKioskApiUrl = (path) => `${resolveApiUrl()}${path}`;

const resolveKioskWebSocketUrl = (token) => {
  const apiUrl = resolveApiUrl();

  if (apiUrl.startsWith("http://") || apiUrl.startsWith("https://")) {
    const apiOrigin = new URL(apiUrl).origin;
    const wsProtocol = apiOrigin.startsWith("https://") ? "wss" : "ws";
    return `${wsProtocol}://${new URL(apiOrigin).host}/ws/kiosk?token=${encodeURIComponent(token)}`;
  }

  const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
  return `${wsProtocol}://${window.location.host}/ws/kiosk?token=${encodeURIComponent(token)}`;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return d.toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MatchSlide = ({ slide }) => {
  const matches = slide.matches || [];
  const isWelcome = slide.type === "WELCOME_SCREEN";
  return (
    <div className="kiosk-info-slide">
      <h1 className="kiosk-info-slide__title">{slide.title}</h1>
      {isWelcome && (
        <p className="kiosk-info-slide__subtitle">
          {new Date().toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
        </p>
      )}
      {matches.length === 0 ? (
        <p className="kiosk-no-matches">Keine Spiele geplant</p>
      ) : (
        <ul className="kiosk-match-list">
          {matches.map((m, i) => (
            <li key={i} className="kiosk-match-item">
              <span className="kiosk-match-item__time">{formatDateTime(m.matchDateTime)}</span>
              <span className="kiosk-match-item__teams">
                {m.leagueTeamName || m.homeTeam} – {m.guestTeam}
              </span>
              {m.venue && <span className="kiosk-match-item__venue">{m.venue}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PairingScreen = ({ token }) => (
  <div className="kiosk-display kiosk-token-screen">
    <div className="kiosk-token-box">
      <h2 className="kiosk-token-box__title">Gerät wird verbunden</h2>
      <p className="kiosk-token-box__hint">
        Diesen Code in der Kiosk-Konfiguration aktivieren:
      </p>
      <div className="kiosk-token-box__code">{shortCode(token)}</div>
      <p className="kiosk-token-box__full">{token}</p>
      <div className="kiosk-token-box__spinner" />
    </div>
  </div>
);

const KioskDisplay = () => {
  const [token] = useState(() => {
    let t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      t = generateToken();
      localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
  });

  const [activated, setActivated] = useState(false);
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);
  const pollRef = useRef(null);
  const loadSlidesRef = useRef(null);
  const advanceRef = useRef(null);
  const retreatRef = useRef(null);

  // Register token on mount, then poll for activation
  useEffect(() => {
    fetch(resolvePublicKioskApiUrl("/public/kiosk/register"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    }).catch(() => {});

    const poll = async () => {
      try {
        const res = await fetch(resolvePublicKioskApiUrl(`/public/kiosk/status?token=${encodeURIComponent(token)}`));
        if (res.ok) {
          const data = await res.json();
          if (data.active) {
            setActivated(true);
            clearInterval(pollRef.current);
          }
        }
      } catch {}
    };

    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [token]);

  // WebSocket connection for CEC display control (after activation)
  useEffect(() => {
    if (!activated) return;
    const wsUrl = resolveKioskWebSocketUrl(token);
    let ws;
    let reconnectTimeout;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        console.log("Kiosk websocket connected", wsUrl);
      };
      ws.onmessage = (event) => {
        console.log("Command received: " + event.data);
        if (event.data === "DISPLAY_ON") {
          fetch(`http://localhost:${CEC_LOCAL_PORT}/cec-on`, { mode: "no-cors" }).catch(() => {});
        } else if (event.data === "DISPLAY_OFF") {
          fetch(`http://localhost:${CEC_LOCAL_PORT}/cec-off`, { mode: "no-cors" }).catch(() => {});
        } else if (event.data === "RELOAD_SLIDES") {
          if (loadSlidesRef.current) loadSlidesRef.current();
        } else if (event.data === "SLIDE_NEXT") {
          if (advanceRef.current) advanceRef.current();
        } else if (event.data === "SLIDE_PREV") {
          if (retreatRef.current) retreatRef.current();
        }
      };
      ws.onerror = (error) => {
        console.error("Kiosk websocket error", error);
      };
      ws.onclose = () => {
        reconnectTimeout = setTimeout(connect, 5000);
        console.error("Kiosk websocket closed");
      };
    };

    connect();
    return () => {
      clearTimeout(reconnectTimeout);
      if (ws) ws.onclose = null;
      if (ws) ws.close();
    };
  }, [activated, token]); // eslint-disable-line

  const loadSlides = useCallback(async () => {
    if (!activated) return;
    try {
      const res = await fetch(resolvePublicKioskApiUrl(`/public/kiosk/slides?token=${encodeURIComponent(token)}`));
      if (!res.ok) return;
      const data = await res.json();
      setSlides(Array.isArray(data) ? data : []);
    } catch {}
  }, [activated, token]);

  useEffect(() => {
    loadSlidesRef.current = loadSlides;
  }, [loadSlides]);

  useEffect(() => {
    loadSlides();
    const refresh = setInterval(loadSlides, 5 * 60 * 1000);
    return () => clearInterval(refresh);
  }, [loadSlides]);

  const advance = useCallback(() => {
    setCurrentIndex(prev => (slides.length <= 1 ? prev : (prev + 1) % slides.length));
    setProgress(0);
  }, [slides.length]);

  const retreat = useCallback(() => {
    setCurrentIndex(prev => (slides.length <= 1 ? prev : (prev - 1 + slides.length) % slides.length));
    setProgress(0);
  }, [slides.length]);

  useEffect(() => { advanceRef.current = advance; }, [advance]);
  useEffect(() => { retreatRef.current = retreat; }, [retreat]);

  useEffect(() => {
    if (slides.length === 0) return;
    const slide = slides[currentIndex];
    if (!slide) return;
    const duration = (slide.displaySeconds || 30) * 1000;
    startTimeRef.current = Date.now();
    setProgress(0);
    if (slides.length <= 1) return;
    timerRef.current = setTimeout(advance, duration);
    const tick = () => {
      const elapsed = Date.now() - startTimeRef.current;
      setProgress(Math.min((elapsed / duration) * 100, 100));
    };
    progressRef.current = setInterval(tick, 200);
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [currentIndex, slides, advance]);

  if (!activated) return <PairingScreen token={token} />;

  if (slides.length === 0) {
    return (
      <div className="kiosk-display" style={{ color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }}>
        TG Thedinghausen
      </div>
    );
  }

  const slide = slides[currentIndex];
  if (!slide) return null;

  return (
    <div className="kiosk-display" onClick={advance} style={{ cursor: "pointer" }}>
      <div className="kiosk-slide">
        {(slide.type === "WELCOME_SCREEN" || slide.type === "HOME_MATCHES") && <MatchSlide slide={slide} />}
        {slide.type === "IMAGE" && <img src={slide.contentUrl} alt={slide.title} className="kiosk-image-slide" />}
        {slide.type === "PDF" && <iframe src={slide.contentUrl} title={slide.title} className="kiosk-embed-slide" />}
        {slide.type === "WEBSITE" && <iframe src={slide.contentUrl} title={slide.title} className="kiosk-embed-slide" sandbox="allow-scripts allow-same-origin" />}
      </div>
      {slides.length > 1 && (
        <div className="kiosk-progress">
          <div
            className="kiosk-progress__bar"
            style={{ width: `${progress}%`, transition: progress === 0 ? "none" : "width 0.2s linear" }}
          />
        </div>
      )}
    </div>
  );
};

export default KioskDisplay;
